// db-compare.js

let compareModalEscapeHandler;
let twoProductControlsInjected = false;

function generateVisualSpecRows(product, tagFields, limit = null) {
  const totalSet = selectedProductsForComparison;
  const isMousepad = product.productType === "Mousepads";

  const expandedFields = tagFields.flatMap((field) => {
    if (field.label === "Dims") {
      return [
        {
          label: "Length (mm)",
          baseLabel: "Length",
          prefix: field.prefix,
          part: 0,
        },
        {
          label: "Width (mm)",
          baseLabel: "Width",
          prefix: field.prefix,
          part: 1,
        },
        {
          label: isMousepad ? "Thickness (mm)" : "Height (mm)",
          baseLabel: isMousepad ? "Thickness" : "Height",
          prefix: field.prefix,
          part: 2,
        },
      ];
    }
    return [field];
  });

  const rows = expandedFields.map((field) => {
    let display = "—";

    if (field.part !== undefined) {
      const dims = product.tags.find((t) => t.startsWith(field.prefix));
      const parts = dims
        ? dims
            .replace(field.prefix, "")
            .replace(/mm/gi, "")
            .split(/[x×]/)
            .map((s) => s.trim())
        : [];
      display = parts[field.part] || "—";
    } else {
      const tag = product.tags.find((t) => t.startsWith(field.prefix));
      display = tag ? tag.replace(field.prefix, "") : "—";

      if (field.label.toLowerCase() === "colour" && display !== "—") {
        const value = display;
        const directCss = colourMap[value];
        let swatchHtml;
        if (directCss) {
          swatchHtml = `<span class="colour-half" style="background:${directCss};"></span>`;
        } else {
        const parts = value
          .split(/\s*•\s*|\s*,\s*|\s*\/\s*/)
          .map((v) => v.trim())
          .filter(Boolean);
        
        const cssParts = parts.map(
          (label) => colourMap[label] || label.replace(/\s+/g, "").toLowerCase()
        );
        
        swatchHtml = cssParts
          .map(
            (c) =>
              `<span style="flex:1; background:${c}; height:100%;"></span>`
          )
          .join("");
        }

        display = `
          <div style="display: flex; align-items: center;">
              <span class="colour-inline-swatch" style="display:inline-flex;align-items:center; overflow: hidden;">
                ${swatchHtml}
              </span>
              <span style="margin-left:6px">${escapeHTML(value)}</span>
            </div>
          `;
      }
    }

    let diffBadge = "";
    const isSecondProduct =
      totalSet.length === 2 && product.id === totalSet[1].id;

    if (isSecondProduct) {
      const [pA, pB] = totalSet;
      const base = pA;
      const target = pB;

      const tagBase = base.tags.find((t) => t.startsWith(field.prefix));
      const tagTarget = target.tags.find((t) => t.startsWith(field.prefix));

      let valBase = tagBase
        ? tagBase.replace(field.prefix, "").replace(/[^\d.-]/g, "")
        : "";
      let valTarget = tagTarget
        ? tagTarget.replace(field.prefix, "").replace(/[^\d.-]/g, "")
        : "";

      if (field.part !== undefined) {
        const baseDims = tagBase
          ? tagBase
              .replace(field.prefix, "")
              .replace(/mm/gi, "")
              .split(/[x×]/)
              .map((s) => s.trim())
          : [];
        const targetDims = tagTarget
          ? tagTarget
              .replace(field.prefix, "")
              .replace(/mm/gi, "")
              .split(/[x×]/)
              .map((s) => s.trim())
          : [];
        valBase = baseDims[field.part] || "";
        valTarget = targetDims[field.part] || "";
      }

      if (!isNaN(valBase) && !isNaN(valTarget)) {
        const delta = parseFloat(valTarget) - parseFloat(valBase);
        const abs = Math.abs(delta);
        if (abs >= 1) {
          const hasDecimals = valBase.includes(".") || valTarget.includes(".");
          const roundedDelta = hasDecimals
            ? delta.toFixed(1)
            : Math.round(delta);
          const arrow = delta > 0 ? "▲" : "▼";
          const color = delta > 0 ? "#4caf50" : "#f44336";
          diffBadge = `<span class="diff-badge" style="color: ${color}; font-weight: bold; margin-right: 6px;">${arrow}${roundedDelta}</span>`;
        }
      }
    }

    const allVals = totalSet.map((p) => {
      if (field.part !== undefined) {
        const dimsTag = p.tags.find((t) => t.startsWith(field.prefix));
        if (!dimsTag) return null;
        const parts = dimsTag
          .replace(field.prefix, "")
          .replace(/mm/gi, "")
          .split(/[x×]/)
          .map((s) => s.trim());
        return parts[field.part] || null;
      } else {
        const tag = p.tags.find((t) => t.startsWith(field.prefix));
        return tag ? tag.replace(field.prefix, "").trim() : null;
      }
    });

    const validVals = allVals.filter((v) => v !== null);
    const allMatch =
      validVals.length > 1 && validVals.every((v) => v === validVals[0]);
    const matchClass = allMatch ? "match" : "mismatch";

    return `
      <div class="spec-row ${matchClass}">
        <span>${escapeHTML(field.label)}</span>
        <strong>${diffBadge}${display}</strong>
      </div>
    `;
  });

  return limit ? rows.slice(0, limit).join("") : rows.join("");
}

function generateGroupedSpecGrid(
  products,
  fieldsByGroup,
  showOnlyDiffs = false
) {
  const numProducts = products.length;
  let html = "";

  for (const [groupName, fields] of Object.entries(fieldsByGroup)) {
    if (!fields.length) continue;

    let groupHtml = "";
    let groupHasContent = false;

    fields.forEach((field) => {
      const label = escapeHTML(field.label);
      const prefix = field.prefix;
      const values = products
        .map((p, idx) => {
          let display = "—";
          let diffBadge = "";
          const tag = p.tags.find((t) => t.startsWith(prefix));

          if (field.part !== undefined) {
            const dims = tag
              ? tag
                  .replace(prefix, "")
                  .replace(/mm/gi, "")
                  .split(/[x×]/)
                  .map((s) => s.trim())
              : [];
            display = dims[field.part] || "—";
          } else if (tag) {
            display = tag.replace(prefix, "") || "—";

            if (field.label.toLowerCase() === "colour" && display !== "—") {
              const value = display;
              const directCss = colourMap[value];
              let swatchHtml;
              if (directCss) {
                swatchHtml = `<div class="colour-half" style="background:${directCss};"></div>`;
              } else {
                const parts = value
                  .split(/\s*•\s*|\s*,\s*|\s*\/\s*/)
                  .map((v) => v.trim())
                  .filter(Boolean)
                  .slice(0, 2);
                const cssParts = parts.map(
                  (label) =>
                    colourMap[label] || label.replace(/\s+/g, "").toLowerCase()
                );
                swatchHtml = cssParts
                  .map(
                    (c) =>
                      `<div class="colour-half" style="background:${c};"></div>`
                  )
                  .join("");
              }
              display = `
              <span class="colour-inline-swatch" style="display:inline-flex;align-items:center; overflow: hidden;">
                ${swatchHtml}
              </span>
              <span style="margin-left:6px">${escapeHTML(value)}</span>
            `;
            }
          }

          if (products.length === 2 && display !== "—") {
            const [valA, valB] = products.map((prod) => {
              const tag = prod.tags.find((t) => t.startsWith(prefix));
              if (!tag) return null;

              if (field.part !== undefined) {
                const parts = tag
                  .replace(prefix, "")
                  .replace(/mm/gi, "")
                  .split(/[x×]/)
                  .map((s) => s.trim());
                return parts[field.part] || null;
              } else {
                return tag
                  .replace(prefix, "")
                  .replace(/[^\d.-]/g, "")
                  .trim();
              }
            });

            if (!isNaN(valA) && !isNaN(valB)) {
              const delta = parseFloat(valB) - parseFloat(valA);
              const abs = Math.abs(delta);
              if (abs >= 1) {
                const rounded = Math.abs(delta).toFixed(1);
                const arrow = delta > 0 ? "▲" : "▼";
                const color = delta > 0 ? "#4caf50" : "#f44336";
                diffBadge = `<span class="diff-badge" style="color: ${color}; font-weight: bold; margin-right: 6px;">${arrow}${rounded}</span>`;
              }
            }
          }

          if (display !== "—") groupHasContent = true;
          return `<div class="spec-val">${diffBadge}${display}</div>`;
        })
        .join("");

      const allVals = products.map((p) => {
        const tag = p.tags.find((t) => t.startsWith(prefix));
        if (!tag) return null;

        if (field.part !== undefined) {
          const parts = tag
            .replace(prefix, "")
            .replace(/mm/gi, "")
            .split(/[x×]/)
            .map((s) => s.trim());
          return parts[field.part] || null;
        } else {
          return tag.replace(prefix, "").trim();
        }
      });
      const validVals = allVals.filter((v) => v !== null);
      const allMatch =
        validVals.length > 1 && validVals.every((v) => v === validVals[0]);
      const matchClass = allMatch ? "match" : "mismatch";

      if (!showOnlyDiffs || matchClass === "mismatch") {
        groupHtml += `
          <div class="spec-grid-row ${matchClass}" style="display: contents;">
            <div class="spec-label">${label}</div>
            ${values}
          </div>
        `;
      }
    });

    if (groupHtml.trim() !== "") {
      html += `
        <div class="spec-grid-section" style="display: contents;">
        <div class="spec-grid-heading collapsible-group" data-group="${groupName}" style="font-weight: bold; cursor: pointer; grid-column: 2 / span ${numProducts}; margin-top: 16px; display: flex; justify-content: space-between; align-items: center;">
          <span style="flex: 1; text-align: center;">${groupName}</span>
          <span class="collapse-icon" style="flex-shrink: 0;">▲</span>
        </div>
          ${groupHtml}
        </div>
      `;
    }
  }

  return html;
}

function openCompareModal() {
  if (selectedProductsForComparison.length < 2) {
    showToast("Select at least 2 products to compare.", "error");
    return;
  }

  const defaultSpecs = [
    //"Size",
    "Surface",
    "Glide Type",
    "Firmness",
    "Texture",
    "Base",
  ];

  const specGroups = {
    Dimensions: [
      //"Size",
      "Dims",
      "Length (mm)",
      "Width (mm)",
      "Height (mm)",
      "Thickness (mm)",
    ],
    Performance: [
      "Surface",
      "Glide Type",
      "Firmness",
      "Texture",
      "Coating",
      "Static Friction",
      "Dynamic Friction",
    ],
    "Base & Build": ["Base", "Stitched", "Washable", "Packaged"],
    Style: ["Colour", "Graphics", "Lighting"],
    "Good to know": ["Game Type", "Stock", "Price", "Warranty"],
  };

  const modal = document.getElementById("compareModal");
  const content = modal.querySelector("#compareProductsContainer");
  const compareSet = selectedProductsForComparison.slice(0, 4);
  const tagFields = getTagFieldsForType(currentProductType);

  const defaultTagFields = defaultSpecs
    .map((label) => tagFields.find((f) => f.label === label))
    .filter(Boolean);
  const defaultCount = defaultTagFields.length;

  const ids = selectedProductsForComparison.map((p) => p.id);
  const encoded = btoa(JSON.stringify(ids));
  const typeParam = encodeURIComponent(currentProductType);
  const shareUrl = `${window.location.origin}${window.location.pathname}?type=${typeParam}&compare=${encoded}`;

  const preserveState = {
    isExpanded: false,
    showOnlyDiffs: false,
  };

  const collapsedGrid = document.getElementById("collapsedSpecGrid");
  const expandedGrid = document.getElementById("expandedSpecGrid");
  const diffCheckbox3 = document.getElementById("toggleDiffCheckbox3");

  if (collapsedGrid && expandedGrid) {
    preserveState.isExpanded = expandedGrid.style.display === "grid";
  }
  if (diffCheckbox3) {
    preserveState.showOnlyDiffs = diffCheckbox3.checked;
  }

  const thumbToggleCheckbox = document.getElementById(
    "toggleThumbnailsCheckbox"
  );
  const preserveThumbs = thumbToggleCheckbox
    ? thumbToggleCheckbox.checked
    : true;

  if (compareSet.length === 2) {
    content.innerHTML = `
    <div id="compareModalExportContent" style="margin: -20px; padding: 40px; background: linear-gradient(100deg, #eee 50%, #f6f6f6 50%);">
      <div class="compare-2-flex">
          <div class="compare-card main-product">
                <img src="${
                  compareSet[0].featuredImage?.url || ""
                }" class="compare-img" crossorigin="anonymous" />
           <h2 title="${escapeHTML(compareSet[0].title)}">${escapeHTML(getCleanProductTitle(compareSet[0]))}</h2>
          <div class="spec-group" data-product-index="0">
            ${generateVisualSpecRows(
              compareSet[0],
              defaultTagFields,
              defaultCount
            )}
          </div>
        </div>
        <div class="compare-center">
          <div id="ordinalSlidersContainer"></div>
          <h3>Vs</h3>
          <div id="matchBarContainer" class="match-bar-container">
            <div id="matchBarFill" class="match-bar-fill"></div>
            <span id="matchBarLabel" class="match-bar-label"></span>
          </div>
        </div>
          <div class="compare-card">
                <img src="${
                  compareSet[1].featuredImage?.url || ""
                }" class="compare-img" crossorigin="anonymous" />
            <h2 title="${escapeHTML(compareSet[0].title)}">${escapeHTML(getCleanProductTitle(compareSet[1]))}</h2>
            <div class="spec-group" data-product-index="1">
              ${generateVisualSpecRows(
                compareSet[1],
                defaultTagFields,
                defaultCount
              )}
            </div>
          </div>
        </div>
        <div class="compare-footer hide-on-export">
          <div style="min-width: 50%; padding-right: 20px;">
            <div class="savecomparison-block">
              <input type="text" id="comparisonName" class="db-modal-field" placeholder="Name your comparison..." />
              <button id="saveComparisonBtn">Save</button>
            </div>
            <div id="shareLinkContainer" style="display: flex;">
              <button id="copyCompareLinkBtn">🔗 Copy</button>
              <button id="nativeCompareShareBtn">📤 Share</button>
              <button id="exportCompareBtn">📥 Export PNG</button>
            </div>
          </div>
          <div id="addProductSearch" style="padding-left: 20px; min-width: 50%; position: relative;">
            <input type="text" id="addProductInput" placeholder="Compare another product..." class="db-modal-search" />
            <div id="addProductResults" class="db-modal-search-dropdown"></div>
          </div>
        </div>
      </div>
    `;
  } else {
    document.documentElement.style.setProperty(
      "--compare-cols",
      compareSet.length
    );

    const expandedFieldsByGroup = {
      Dimensions: tagFields.flatMap((f) => {
        if (f.label === "Dims") {
          return [
            {
              label: "Length (mm)",
              baseLabel: "Length",
              prefix: f.prefix,
              part: 0,
            },
            {
              label: "Width (mm)",
              baseLabel: "Width",
              prefix: f.prefix,
              part: 1,
            },
            {
              label: "Thickness (mm)",
              baseLabel: "Thickness",
              prefix: f.prefix,
              part: 2,
            },
          ];
        }
        return [];
      }),
      Performance: tagFields.filter((f) =>
        [
          "Surface",
          "Glide Type",
          "Firmness",
          "Texture",
          "Coating",
          "Static Friction",
          "Dynamic Friction",
        ].includes(f.label)
      ),
      "Base & Build": tagFields.filter((f) =>
        ["Base", "Stitched", "Washable", "Packaged"].includes(f.label)
      ),
      Style: tagFields.filter((f) =>
        ["Colour", "Graphics", "Lighting"].includes(f.label)
      ),
      "Good to know": tagFields.filter((f) =>
        ["Game Type", "Stock", "Price", "Warranty"].includes(f.label)
      ),
    };

    const defaultFieldsByGroup = {};

    for (const [groupName, fields] of Object.entries(expandedFieldsByGroup)) {
      const filtered = fields.filter((f) => defaultSpecs.includes(f.label));
      if (!filtered.length) continue;

      const hasContent = filtered.some((field) => {
        return compareSet.some((product) => {
          const tag = product.tags.find((t) => t.startsWith(field.prefix));
          if (!tag) return false;

          if (field.part !== undefined) {
            const parts = tag
              .replace(field.prefix, "")
              .replace(/mm/gi, "")
              .split(/[x×]/);
            const value = parts[field.part]?.trim();
            return value && value !== "—";
          } else {
            const value = tag.replace(field.prefix, "").trim();
            return value && value !== "—";
          }
        });
      });

      if (hasContent) {
        defaultFieldsByGroup[groupName] = filtered;
      }
    }

    content.innerHTML = `
    <div id="compareModalExportContent" style="padding: 0 20px 20px 30px; overflow: visible;">
      <div class="compare-grid-wrapper">
        <div id="thumbnailRow">
          <div class="compare-grid-header" style="display: grid; grid-template-columns: 140px repeat(${
            compareSet.length
          }, 1fr); gap: 10px;">
            <div></div>
            ${compareSet
              .map(
                (p, i) => `
            <div class="compare-card product-thumbnail" draggable="true" data-index="${i}" title="Drag & drop to reorder products">
            <span class="drag-handle hide-on-export">⇄</span>
              <div class="thumbnail-image-wrapper">
                <img src="${
                  p.featuredImage?.url || ""
                }" class="compare-img" crossorigin="anonymous" />
                <button class="remove-compare-btn hide-on-export" data-id="${
                  p.id
                }" title="Remove">×</button>
              </div>
              <h2 title="${escapeHTML(compareSet[0].title)}">${escapeHTML(getCleanProductTitle(p))}</h2>
              <div class="thumbnail-image-wrapper">
              </div>
            </div>
            `
              )
              .join("")}
          </div>
          <div style="display: grid; grid-template-columns: 140px repeat(3, 1fr); gap: 10px;margin-top: 10px;">
          <div></div>
            <button class="hide-on-export" style="text-align: left;" id="expandAllSpecs3Btn">View All Specs</button>
            <div style="text-align: right;">
              <label class="switch-toggle hide-on-export" style="display:inline-flex; align-items:center;">
                <input type="checkbox" id="toggleThumbnailsCheckbox" checked>
                <span class="slider"></span>
                <span class="switch-label" style="margin-left:8px;">Images</span>
              </label>
            </div>
          </div>
        </div>
        <div id="specGridWrapper">
        <div class="spec-grid" id="collapsedSpecGrid" style="display: grid; grid-template-columns: 120px repeat(${
          compareSet.length
        }, 1fr); gap: 10px;">
          ${defaultTagFields
            .map((field) => {
              const label = escapeHTML(field.label);
              const allVals = compareSet.map((p) => {
                const tag = p.tags.find((t) => t.startsWith(field.prefix));
                if (!tag) return null;
                if (field.part !== undefined) {
                  const parts = tag
                    .replace(field.prefix, "")
                    .replace(/mm/gi, "")
                    .split(/[x×]/)
                    .map((s) => s.trim());
                  return parts[field.part] || null;
                } else {
                  return tag.replace(field.prefix, "").trim();
                }
              });
              const validVals = allVals.filter((v) => v !== null);
              const allMatch =
                validVals.length > 1 &&
                validVals.every((v) => v === validVals[0]);
              const matchClass = allMatch ? "match" : "mismatch";

              const values = compareSet
                .map((product) => {
                  let display = "—";
                  const tag = product.tags.find((t) =>
                    t.startsWith(field.prefix)
                  );

                  if (field.part !== undefined) {
                    const parts = tag
                      ? tag
                          .replace(field.prefix, "")
                          .replace(/mm/gi, "")
                          .split(/[x×]/)
                          .map((s) => s.trim())
                      : [];
                    display = parts[field.part] || "—";
                  } else if (tag) {
                    display = tag.replace(field.prefix, "") || "—";

                    if (
                      field.label.toLowerCase() === "colour" &&
                      display !== "—"
                    ) {
                      const value = display;
                      const directCss = colourMap[value];
                      let swatchHtml;
                      if (directCss) {
                        swatchHtml = `<div class="colour-half" style="background:${directCss};"></div>`;
                      } else {
                        const parts = value
                          .split(/\s*•\s*|\s*,\s*|\s*\/\s*/)
                          .map((v) => v.trim())
                          .filter(Boolean)
                          .slice(0, 2);
                        const cssParts = parts.map(
                          (label) =>
                            colourMap[label] ||
                            label.replace(/\s+/g, "").toLowerCase()
                        );
                        swatchHtml = cssParts
                          .map(
                            (c) =>
                              `<div class="colour-half" style="background:${c};"></div>`
                          )
                          .join("");
                      }
                      display = `
                      <div class="colour-block sidebar-colour-swatch">
                      ${swatchHtml}
                      <span style="margin-left:6px">${escapeHTML(value)}</span>
                    </span>
                  `;
                    }
                  }

                  return `<div class="spec-val">${display}</div>`;
                })
                .join("");

              return `
              <div class="spec-grid-row ${matchClass}" style="display: contents;">
                <div class="spec-label">${label}</div>
                ${values}
              </div>
            `;
            })
            .join("")}
        </div>
          <div class="spec-grid" id="expandedSpecGrid" style="display: none; grid-template-columns: 120px repeat(${
            compareSet.length
          }, 1fr); gap: 10px;">
            ${generateGroupedSpecGrid(compareSet, expandedFieldsByGroup)}
          </div>
        </div>
        <div class="compare-footer hide-on-export">
        <div style="min-width: 50%; padding-right: 20px;">
          <div class="savecomparison-block">
            <input type="text" id="comparisonName" class="db-modal-field" placeholder="Name your comparison..." />
            <button id="saveComparisonBtn">Save</button>
          </div>
          <div id="shareLinkContainer">
            <button id="copyCompareLinkBtn">🔗 Copy Link</button>
            <button id="nativeCompareShareBtn">📤 Share</button>
            <button id="exportCompareBtn">📥 Export PNG</button>
          </div>
        </div>
        <div id="addProductSearch" style="padding-left: 20px; min-width: 50%; position: relative;">
          <input type="text" id="addProductInput" placeholder="Compare another product..." class="db-modal-search" />
          <div id="addProductResults" class="db-modal-search-dropdown"></div>
        </div>
      </div>
    </div>
  </div>
`;

    const searchBar = document.getElementById("addProductSearch");
    if (searchBar) {
      searchBar.style.display = compareSet.length < 4 ? "block" : "none";
    }

    document.querySelectorAll(".remove-compare-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idToRemove = btn.dataset.id;
        selectedProductsForComparison = selectedProductsForComparison.filter(
          (p) => p.id !== idToRemove
        );

        saveComparisonToStorage();
        updateCompareTrayUI();
        updateCompareButtonsVisibility();
        updateMatchPercent();

        document
          .querySelectorAll(
            `.compare-checkbox[data-product-id="${idToRemove}"]`
          )
          .forEach((cb) => (cb.checked = false));
        document
          .querySelectorAll(`.compare-toggle[data-product-id="${idToRemove}"]`)
          .forEach((btn) => btn.classList.remove("active"));
        document
          .querySelectorAll(`tr[data-product-id="${idToRemove}"]`)
          .forEach((row) => {
            row.classList.remove("row-selected", "row-disabled");
            Array.from(row.children).forEach((cell) =>
              cell.classList.remove("highlight-diff")
            );
          });

        document.querySelectorAll("tr[data-product-id]").forEach((row) => {
          const id = row.getAttribute("data-product-id");
          const isSelected = selectedProductsForComparison.some(
            (p) => p.id === id
          );
          row.classList.remove("row-disabled");
          if (selectedProductsForComparison.length >= 4 && !isSelected) {
            row.classList.add("row-disabled");
          }
        });

        openCompareModal();
      });
    });

    const expandBtn = document.getElementById("expandAllSpecs3Btn");
    const diffToggleWrapper = document.createElement("div");
    diffToggleWrapper.innerHTML = `
      <label class="switch-toggle hide-on-export" style="margin-bottom:10px; display:inline-flex; align-items:center; width: 100%; justify-content: center;">
        <input type="checkbox" id="toggleDiffCheckbox3">
        <span class="slider"></span>
        <span class="switch-label" style="margin-left:8px;">≠ Only</span>
      </label>
    `;
    expandBtn.parentElement.insertBefore(
      diffToggleWrapper,
      expandBtn.nextSibling
    );
    const collapsedGrid = document.getElementById("collapsedSpecGrid");
    const expandedGrid = document.getElementById("expandedSpecGrid");

    expandBtn.addEventListener("click", () => {
      const isExpanded = expandedGrid.style.display === "grid";
      expandedGrid.style.display = isExpanded ? "none" : "grid";
      collapsedGrid.style.display = isExpanded ? "grid" : "none";
      expandBtn.textContent = isExpanded ? "View All Specs" : "View Less Specs";
    });

    const diffCheckbox3 = document.getElementById("toggleDiffCheckbox3");
    diffCheckbox3.addEventListener("change", (e) => {
      const hideMatches = e.target.checked;
      const expandBtn = document.getElementById("expandAllSpecs3Btn");
      const collapsedGrid = document.getElementById("collapsedSpecGrid");
      const expandedGrid = document.getElementById("expandedSpecGrid");

      if (hideMatches && expandedGrid.style.display === "none") {
        expandBtn.click();
      }

      requestAnimationFrame(() => {
        document
          .querySelectorAll("#expandedSpecGrid .spec-grid-row")
          .forEach((row) => {
            const isMismatch = row.classList.contains("mismatch");
            row.style.display = hideMatches
              ? isMismatch
                ? "contents"
                : "none"
              : "contents";
          });

        document
          .querySelectorAll("#expandedSpecGrid .spec-grid-section")
          .forEach((section) => {
            const rows = section.querySelectorAll(".spec-grid-row");
            const visible = Array.from(rows).some(
              (r) => r.style.display !== "none"
            );
            const heading = section.querySelector(".spec-grid-heading");
            if (heading) heading.style.display = visible ? "flex" : "none";
          });
      });
    });

    if (preserveState.isExpanded) {
      expandBtn.click();
    }
    if (preserveState.showOnlyDiffs) {
      diffCheckbox3.checked = true;
      diffCheckbox3.dispatchEvent(new Event("change"));
    }
  }

    // inject diff-only toggle & “View All Specs” into the 2-product modal, once per render
  if (compareSet.length === 2) {
    requestAnimationFrame(() => {
      const center = document.querySelector(".compare-center");
      if (!center || center.querySelector("#toggleDiffCheckbox")) return;

      // 1) Diff-only toggle
      const toggleWrapper = document.createElement("div");
      toggleWrapper.innerHTML = `
        <label class="switch-toggle hide-on-export" style="margin-top:10px;display:flex;justify-content:center;">
          <input type="checkbox" id="toggleDiffCheckbox">
          <span class="slider"></span>
          <span class="switch-label">≠ Only</span>
        </label>
      `;
      center.appendChild(toggleWrapper);
      const diffCheckbox = toggleWrapper.querySelector("#toggleDiffCheckbox");
      diffCheckbox.addEventListener("change", (e) => {
        const hide = e.target.checked;
        // auto-expand if needed
        const expandBtn = document.getElementById("expandAllSpecs");
        if (hide && expandBtn.textContent === "View All Specs") expandBtn.click();
        // show only mismatches
        document
          .querySelectorAll(".compare-2-flex .spec-row")
          .forEach(r => r.style.display = hide
            ? (r.classList.contains("mismatch") ? "" : "none")
            : ""
          );
        
          // 2) Hide any empty group containers
          document
            .querySelectorAll(".compare-2-flex .spec-category")
            .forEach(cat => {
              const anyVisible = Array.from(cat.querySelectorAll(".spec-row"))
                .some(r => r.style.display !== "none");
              cat.style.display = hide && !anyVisible ? "none" : "";
            });
        
        requestAnimationFrame(syncSpecRowHeights);
      });

      // 2) View All Specs toggle
      const expandAllBtn = document.createElement("button");
      expandAllBtn.id = "expandAllSpecs";
      expandAllBtn.textContent = "View All Specs";
      expandAllBtn.classList.add("hide-on-export");
      expandAllBtn.style.cssText = "display:block;margin:10px auto;text-decoration:underline;";
      center.appendChild(expandAllBtn);

      let allExpanded = false;
      expandAllBtn.addEventListener("click", () => {
        allExpanded = !allExpanded;
        expandAllBtn.textContent = allExpanded ? "View Less Specs" : "View All Specs";

        compareSet.forEach((product, idx) => {
          const grp = document.querySelector(`.spec-group[data-product-index="${idx}"]`);
          if (!grp) return;
          if (!allExpanded) {
            grp.innerHTML = generateVisualSpecRows(product, defaultTagFields, defaultCount);
          } else {
            grp.innerHTML = Object.entries(specGroups)
              .map(([groupName, labels]) => {
                const fields = tagFields.filter(f => labels.includes(f.label));
                return fields.length
                  ? `<div class="spec-category"><h4>${groupName}</h4>` +
                      generateVisualSpecRows(product, fields) +
                    `</div>`
                  : "";
              })
              .join("");
          }
        });

        if (!allExpanded && diffCheckbox.checked) {
          diffCheckbox.checked = false;
          diffCheckbox.dispatchEvent(new Event("change"));
        }
        requestAnimationFrame(syncSpecRowHeights);
      });
    });
  }

  openModal("compareModal");

  const modalEl = document.getElementById("compareModal");
  modalEl.setAttribute("tabindex", "-1");
  modalEl.focus();

  document.removeEventListener("keydown", compareModalEscapeHandler);
  compareModalEscapeHandler = (e) => {
    const isEscape =
      e.key === "Escape" || e.key === "Esc" || e.keyCode === 27;
    if (isEscape) {
      closeModal("compareModal");
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", compareModalEscapeHandler);
    }
  };
  document.addEventListener("keydown", compareModalEscapeHandler);

  if (compareSet.length === 2) {
    const percent = calculateProductMatchPercent(compareSet[0], compareSet[1]);
    const fill = document.getElementById("matchBarFill");
    const label = document.getElementById("matchBarLabel");
    const container = document.getElementById("matchBarContainer");
    if (fill && label && container) {
      fill.style.width = `${percent}%`;
      label.textContent = `${percent}% Match`;
      let color =
        percent >= 80 ? "#4caf50" : percent >= 50 ? "#ff9800" : "#f44336";
      fill.style.backgroundColor = color;
      container.style.borderColor = color;
    }
  }

  if (compareSet.length === 2) {
    renderOrdinalSliders(compareSet, tagFields);
  }

  requestAnimationFrame(syncSpecRowHeights);
           
  document.getElementById("saveComparisonBtn").onclick = () => {
    const name = document.getElementById("comparisonName").value.trim();
    if (!name) {
      showToast("Please enter a name for this comparison.", "error");
      return;
    }
    savedComparisons.push({
      name,
      type: currentProductType,
      products: selectedProductsForComparison.map((p) => ({ id: p.id })),
    });
    saveLocal("savedComparisons", savedComparisons);
    showToast(`Saved as "${name}"`, "info");
    renderSavedComparisons();

    const panel = document.getElementById("columnTogglePanel");
    const isOpen = panel && panel.style.transform === "translateX(0px)";
    if (!isOpen) {
      toggleColumnPanel();
    }
    closeModal("compareModal");
  };
  document.getElementById("copyCompareLinkBtn").onclick = () =>
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => showToast("Link copied!", "info"));
  document.getElementById("nativeCompareShareBtn").onclick = () => {
    if (navigator.share) {
      navigator
        .share({
          title: document.title,
          text: "Check out this comparison:",
          url: shareUrl,
        })
        .catch(() => showToast("Sharing failed or cancelled.", "error"));
    } else {
      showToast("Sharing not supported on this device.", "error");
    }
  };

  function updateCompareShareButtonVisibility() {
    const btn = document.getElementById('nativeCompareShareBtn');
    if (!btn) return;
    // only show if Web Share API is available
    btn.style.display = navigator.share ? 'inline-block' : 'none';
  }

  updateCompareShareButtonVisibility();

  document.querySelectorAll(".collapsible-group").forEach((heading) => {
    heading.addEventListener("click", () => {
      const groupEl = heading.closest(".spec-grid-section");
      if (!groupEl) return;

      const rows = [...groupEl.querySelectorAll(".spec-grid-row")];
      const isHidden = rows.every((r) => r.style.display === "none");
      rows.forEach((r) => (r.style.display = isHidden ? "contents" : "none"));

      const icon = heading.querySelector(".collapse-icon");
      if (icon) icon.textContent = isHidden ? "▲" : "▼";
    });
  });

  const searchInput = document.getElementById("addProductInput");
  const resultsBox = document.getElementById("addProductResults");

  if (searchInput) {
    searchInput.addEventListener("input", () => {
      const q = searchInput.value.toLowerCase().trim();
      const terms = q.split(/\s+/).filter(Boolean);
      if (terms.length < 1) {
        resultsBox.style.display = "none";
        return;
      }

      const matches = currentFilteredProducts.filter((p) => {
        const title = p.title.toLowerCase();
        return (
          terms.every((term) => title.includes(term)) &&
          !selectedProductsForComparison.find((sel) => sel.id === p.id)
        );
      });

      resultsBox.innerHTML = matches
        .map(
          (p) => `
      <div class="result-item" data-id="${
        p.id
      }" style="padding: 6px; cursor: pointer;">
        ${escapeHTML(p.title)}
      </div>
    `
        )
        .join("");

      resultsBox.style.display = matches.length ? "block" : "none";
    });

    resultsBox.addEventListener("click", (e) => {
      const id = e.target.closest(".result-item")?.dataset?.id;
      if (!id) return;

      const match = productMap[id];
      if (!match) return;

      selectedProductsForComparison.push(match);
      saveComparisonToStorage();
      updateCompareTrayUI();
      updateMatchPercent();

      document
        .querySelectorAll(`.compare-checkbox[data-product-id="${id}"]`)
        .forEach((cb) => {
          cb.checked = true;
          cb.classList.add("pop-animate");
          setTimeout(() => cb.classList.remove("pop-animate"), 300);

          const row = cb.closest("tr");
          if (row) row.classList.add("row-selected");
        });

      openCompareModal();
    });

    document.addEventListener("click", (e) => {
      if (!resultsBox.contains(e.target) && e.target !== searchInput) {
        resultsBox.style.display = "none";
      }
    });
  }

  document
    .querySelectorAll(".compare-card.product-thumbnail")
    .forEach((col) => {
      col.addEventListener("dragstart", (e) => {
        dragStartIndex = +col.dataset.index;
        col.classList.add("dragging");
      });

      col.addEventListener("dragover", (e) => e.preventDefault());

      col.addEventListener("drop", (e) => {
        const dropIndex = +col.dataset.index;
        if (dragStartIndex === null || dropIndex === dragStartIndex) return;

        const reordered = [...selectedProductsForComparison];
        const [moved] = reordered.splice(dragStartIndex, 1);
        reordered.splice(dropIndex, 0, moved);
        selectedProductsForComparison = reordered;

        openCompareModal();
      });

      col.addEventListener("dragend", () => {
        document
          .querySelectorAll(".compare-card.product-thumbnail")
          .forEach((c) => c.classList.remove("dragging"));
        dragStartIndex = null;
      });
    });

  const exportBtn = document.getElementById("exportCompareBtn");
  if (exportBtn) {
    exportBtn.addEventListener("click", () => {
      const modalContent = document.getElementById("compareModalExportContent");

      html2canvas(modalContent, {
        backgroundColor: "#ffffff",
        scale: window.devicePixelRatio,
        useCORS: true,
        allowTaint: false,
        ignoreElements: (el) => el.classList.contains("hide-on-export"),
      }).then((canvas) => {
        const link = document.createElement("a");
        link.download = "comparison.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
      });
    });
  }
  const thumbToggle = document.getElementById("toggleThumbnailsCheckbox");
  if (thumbToggle) {
    thumbToggle.addEventListener("change", (e) => {
      document.querySelectorAll(".thumbnail-image-wrapper").forEach((div) => {
        div.style.display = e.target.checked ? "" : "none";
      });
    });
  }
  if (thumbToggle) {
    thumbToggle.checked = preserveThumbs;
    thumbToggle.dispatchEvent(new Event("change"));
  }
  document.body.classList.add("modal-open");
}

/**
 * Renders two simple ordinal sliders (Firmness & Texture)
 * into #ordinalSlidersContainer
 */
function renderOrdinalSliders(compareSet, tagFields) {
  const axes = {
    Firmness: ["Extra Soft", "Soft", "Mid", "Hard"],
    Texture: ["Ultra-smooth", "Smooth", "Slightly Grainy", "Grainy", "Rough"],
  };

  const container = document.getElementById("ordinalSlidersContainer");
  container.innerHTML = "";

  Object.entries(axes).forEach(([axis, values]) => {
    const prefix = (tagFields.find((f) => f.label === axis) || {}).prefix || "";

    const positions = compareSet.map((prod) => {
      const tag = prod.tags.find((t) => t.startsWith(prefix));
      const val = tag ? tag.slice(prefix.length).trim() : null;
      const idx = values.indexOf(val);
      return Math.max(0, idx);
    });

    const maxIdx = values.length - 1;
    const html = `
      <div class="ordinal-axis">
        <div class="axis-label">${axis}</div>
<div class="axis-track">
  ${values
    .map(
      (_, i) =>
        `<span class="tick" style="left:${(i / maxIdx) * 100}%;"></span>`
    )
    .join("")}
  <span class="marker marker-a" style="left:${
    (positions[0] / maxIdx) * 100
  }%;"></span>
  <span class="marker marker-b" style="left:${
    (positions[1] / maxIdx) * 100
  }%;"></span>
</div>
        <div class="axis-ends">
          <span>${values[0]}</span><span>${values[maxIdx]}</span>
        </div>
      </div>
    `;
    container.insertAdjacentHTML("beforeend", html);
  });
}

function saveComparisonToStorage() {
  saveLocal("selectedComparisonProducts", selectedProductsForComparison);
}

function resetCompare() {
  document
    .querySelectorAll(".compare-checkbox")
    .forEach((cb) => (cb.checked = false));
  selectedProductsForComparison = [];
  localStorage.removeItem("selectedComparisonProducts");
  document.getElementById("compareBtn").style.display = "none";
}

function renderSavedComparisons() {
  savedComparisons = loadLocal("savedComparisons", []);
  const container = document.getElementById("savedComparisonsContainer");
  if (!container) return;

  const filtered = savedComparisons
    .map((comp, i) => ({ ...comp, _originalIndex: i }))
    .filter((comp) => comp.type === currentProductType);

  container.innerHTML = filtered.length
    ? `<div class="button-labels">Comparisons</div>` +
      filtered
        .map((comp) => {
          const isActive = comp.name === activeComparisonName;
          const classes = ["saved-list-items"];
          if (isActive) classes.push("active");

          return `
    <div class="saved-list-items${isActive ? " active" : ""}">
      <span class="editable-name" onclick="loadSavedComparison(${
        comp._originalIndex
      })">${comp.name}</span>
      <div class="saved-item-buttons">
        <button onclick="renameComparison('${
          comp.name
        }')" title="Rename">✎</button>
        <button onclick="deleteSavedComparison(${
          comp._originalIndex
        })" title="Delete" class="saved-item-delete">×</button>
      </div>
    </div>
  `;
        })
        .join("")
    : "";

  updateSavedListsEmptyState();
}

function loadSavedComparison(index) {
  const comp = savedComparisons[index];
  if (!comp || !comp.products) return;

  selectedProductsForComparison = comp.products
    .map((p) => productMap[p.id])
    .filter(Boolean);

  activeComparisonName = comp.name;
  renderSavedComparisons();
  renderProductsTable(currentFilteredProducts, currentProductType);
  updateCompareButtonsVisibility();
  updateMatchPercent();
  updateCompareTrayUI();
  document.getElementById("compareTray")?.classList.add("show");
  openCompareModal();
}

function renameComparison(oldName) {
  showInputModal(`Rename "${oldName}" to:`, (newName) => {
    if (!newName || newName === oldName) return;

    const duplicate = savedComparisons.some((c) => c.name === newName);
    if (duplicate) {
      showToast("Name already exists.", "error");
      return;
    }

    const match = savedComparisons.find((c) => c.name === oldName);
    if (!match) return;

    match.name = newName;
    saveLocal("savedComparisons", savedComparisons);
    renderSavedComparisons();
    showToast(`Renamed to "${newName}"`);
  });
}

function deleteSavedComparison(index) {
  showConfirmModal("Delete this saved comparison?", (confirmed) => {
    if (!confirmed) return;
    savedComparisons.splice(index, 1);
    saveLocal("savedComparisons", savedComparisons);
    renderSavedComparisons();
    showToast("Comparison deleted.", "info");
  });
}

function syncSpecRowHeights() {
  const groups = document.querySelectorAll("#compareModal .spec-group");
  if (groups.length < 2) return;

  const rowCount = Math.max(
    ...Array.from(groups).map((g) => g.children.length)
  );

  for (let i = 0; i < rowCount; i++) {
    groups.forEach((group) => {
      const row = group.children[i];
      if (row) row.style.height = "";
    });
  }

  requestAnimationFrame(() => {
    for (let i = 0; i < rowCount; i++) {
      let maxHeight = 0;

      groups.forEach((group) => {
        const row = group.children[i];
        if (row) {
          const h = row.getBoundingClientRect().height;
          if (h > maxHeight) maxHeight = h;
        }
      });

      groups.forEach((group) => {
        const row = group.children[i];
        if (row) row.style.height = maxHeight + "px";
      });
    }
  });
}
